import React, { useEffect, useRef } from "react";
import classNames from "classnames";
import { RichText, Text } from "@sitecore-jss/sitecore-jss-react";
import { getOverallSettingsClasses, loadScrollMagic, mobileIs, componentId } from "../../utils/siteUtils";
import { BackgroundImage } from "../../elements";
import "./style.scss";
import gsap from 'gsap';

const ImmersiveImage = props => {
	const { rendering, fields, params } = props;
  const { title, title2, pc_text, pc_text2, pc_image, m_text, m_text2, m_image, list } = fields;
  const isMobile = mobileIs()
  const rootNode = useRef()
  const imageEl = useRef()
  const textEl = useRef()
  const emptyEl = useRef()
  const titleEl = useRef()
  const subTitleEl = useRef();
  const followUpEl = useRef();
  const bgImgEl = useRef();
  const autoScrollTime = useRef(0);
  const scrollPosition = useRef(0);
  const scrollDirection = useRef(null);
  const easingTimer = useRef(0);
  const parallaxTimer = useRef(0);
  const isUserScrollTimer = useRef(0);
  const isUserScroll = useRef(true);

  useEffect(() => {
    const isMac = navigator.userAgent.includes('Mac');
    const scenes = [];
    let ScrollMagic, controller, scenePara, sceneTitle, sceneImageText
    ScrollMagic = loadScrollMagic()
    controller = new ScrollMagic.Controller();

    const scrolling = (direction) => {
      if (direction !== scrollDirection.current) {
        return false;
      }

      autoScrollTime.current = autoScrollTime.current + 8; // ms

      if ( autoScrollTime.current > 30 * 8) {
        return false;
      }

      window.scrollTo({
        top: window.scrollY + 1 * direction,
        behavior: 'instant'
      });

      setTimeout(() => {
        scrolling(direction);
      }, 8, direction);
    }

    const handleScroll = (e) => {
      if (isMobile || 
        rootNode.current.getBoundingClientRect().top > window.innerHeight || 
        emptyEl.current.getBoundingClientRect().top < 80 ||
        emptyEl.current.getBoundingClientRect().bottom < window.innerHeight ) {
        return false;
      }

      if (!isUserScroll.current) {
        return false;
      }

      const direction = window.scrollY - scrollPosition.current > 0 ? 1 : -1;

      scrollDirection.current = direction;
      clearTimeout(easingTimer.current);
      clearTimeout(isUserScrollTimer.current);

      if (scrollDirection.current !== null && scrollDirection.current !== direction) {
        // Direction changed
        isUserScroll.current = true;
        easingTimer.current = 0;
        return false;
      }

      if (Math.abs(window.scrollY - scrollPosition.current) < 10) {
        easingTimer.current = setTimeout(() => {
          isUserScroll.current = false;
          easingTimer.current = 0;

          scrolling(direction);

          isUserScrollTimer.current = setTimeout(() => {
            autoScrollTime.current = 0;
            isUserScroll.current = true;
          }, isMac ? 300 : 300);
        }, 10);
      }

      scrollPosition.current = window.scrollY;
    };

    window.addEventListener('scroll', handleScroll);

    // Component Title
    const tl = gsap.timeline();
    gsap.set(titleEl.current, {opacity: 0, y: 60});
    gsap.set(subTitleEl.current, {opacity: 0, y: 60});
    tl.add(
      gsap.to(
        titleEl.current, {
          y: 0,
          opacity: 1,
        }
      )
    )
    .add(
      gsap.to(
        subTitleEl.current, {
          y: 0,
          opacity: 1,
        }
      )
    );

    sceneTitle = new ScrollMagic.Scene({
      triggerElement: titleEl.current,
      triggerHook: 1,
      offset: 50,
      duration: 0
    })
    .setTween(tl)
    .addTo(controller);

    scenes.push(sceneTitle);
    
    // Pin scene
    const imgScale = isMobile ? 0.91 : 0.8;
    gsap.set(imageEl.current, { scale: imgScale, borderRadius: 15 });
    gsap.set(emptyEl.current, { width: 1000});

    const scenePin = new ScrollMagic.Scene({
      triggerElement: imageEl.current,
      triggerHook: 0,
      duration: 400
    })
    .setPin(imageEl.current)
    .on('end', function (event) {
      if (list?.length) {
        imageEl.current.classList.add('pinned--element')
      }
    })
    .on('start', function (event) {
      imageEl.current.classList.remove('pinned--element')
    })
    .addTo(controller)

    scenes.push(scenePin);

    // Parallax 
    scenePara = new ScrollMagic.Scene({
      triggerElement: imageEl.current,
      triggerHook: 1,
      duration: window.innerHeight - 80
    })
    .on('progress', function(event) {
      const scale = imgScale + (1 - imgScale) * event.progress.toFixed(4);
      const style = imageEl.current.getAttribute('style');
      const regex = /(scale\()(.*?)(\))/;
      const newStyle= style.replace(regex, `$1${scale.toFixed(3)}$3`)

      setTimeout(() => {
        imageEl.current.setAttribute('style', newStyle);
      }, 0);
      
      const process = 1 - event.progress.toFixed(4);
      const bg = imageEl.current.querySelector('.immersive-image__image-content__image');
      const pos = "translate3d(0, " + Math.round(process * -0.3 * window.innerHeight) + "px, 0)";

      parallaxTimer.current = setTimeout(() => {
        bg.style.transform = pos;
      }, 0);
    })
    .addTo(controller);
    scenes.push(scenePara);

    // Scale
    const tl4 = gsap.timeline();
    
    tl4
    .add(
      gsap.to(
        imageEl.current, {
        borderRadius: 0,
        // scale: 1,
        onComplete: function() {
          this.targets().forEach(elem => elem.classList.add("shadow"));

          if (followUpEl.current) {
            imageEl.current.classList.add('pinned--element')
          }
        },
        onUpdate: function() {
          this.targets().forEach(elem => elem.classList.remove("shadow"));
        }
      })
    );
    
    const scaleScene = new ScrollMagic.Scene({
      triggerElement: imageEl.current,
      triggerHook: 0.5,
      duration: window.innerHeight * 0.5
    })
    .setTween(tl4)
    .addTo(controller);

    scenes.push(scaleScene);

    // Slide Title
    gsap.set(textEl.current, {opacity: 0, y: 60});
    const tl3 = gsap.timeline();
    tl3.add(
      gsap.to(
        textEl.current, {
          y: 0,
          opacity: 1,
        })
    );
    sceneImageText = new ScrollMagic.Scene({
      triggerElement: imageEl.current,
      triggerHook: 0,
      offset: 0,
      duration: 0
    })
    .setTween(tl3)
    .addTo(controller);

    scenes.push(sceneImageText);

    // Ending scene
    const sceneEnding = new ScrollMagic.Scene({
      triggerElement: emptyEl.current,
      triggerHook: 1,
      offset: -20,
      reverse: true,
      duration: 10
    })
    .on('leave', function(event) {
      if (event.scrollDirection === 'FORWARD') {
        const slides = rootNode.current.querySelectorAll('.immersive-image__image-content');
        slides.forEach(element => {
          element.classList.remove('pinned--element');
        });
      } else {
        const slides = rootNode.current.querySelectorAll('.immersive-image__image-content');
        slides.forEach(element => {
          if (!element.classList.contains('last-slide')) {
            element.classList.add('pinned--element');
          }
        });
      }

    }).addTo(controller);

    scenes.push(sceneEnding);

    // Follow up images 
    if (followUpEl.current) {
      const slides = followUpEl.current.querySelectorAll('.immersive-image__image-content');

      // Slides 
      slides.forEach((item, index) => {
        const title = item.querySelector('.immersive-image__text-content');
        const bg = item.querySelector('.immersive-image__image-content');

          gsap.set(title, {opacity: 0, y: 60});

          const tl = new gsap.timeline();
          const tween = tl
            .to(bg, {
              boxShadow: "inset 0px 0px 30vh 30vh rgba(0,0,0,0.44)",
              delay: 0
            })
            .to(title, {
              opacity: 1,
              translateY: 0,
              ease: "power2.out",
              delay: 0,
            }, "<")

          const sceneSlideTitle = new ScrollMagic.Scene({
            triggerElement: item,
            triggerHook: 0,
            offset: -90,
            duration: 0
          })
          .setTween(tween)
          .addTo(controller);

          scenes.push(sceneSlideTitle);


          const scene = new ScrollMagic.Scene({
            triggerElement: item,
            triggerHook: 0,
            reverse: true,
            duration: 400
          })
          .setPin(item, { pushFollowers: true })
          // .setTween(tween)
          .addTo(controller)
          .on('end', function (event) {
            if (!item.classList.contains('last-slide')) {
              item.classList.add('pinned--element');
            }
          })
          .on('start', function (event) {
            item.classList.remove('pinned--element')
          });

          scenes.push(scene);

          // Parallax 
          const scenePara = new ScrollMagic.Scene({
            triggerElement: item,
            triggerHook: 1,
            duration: window.innerHeight - 80
          })
          .on('progress', function(event) {
            const process = 1 - event.progress.toFixed(4);
            const bg = item.querySelector('.immersive-image__image-content__image');
            const pos = "translate3d(0, " + Math.round(process * -0.3 * window.innerHeight) + "px, 0)";

            parallaxTimer.current = setTimeout(() => {
              bg.style.transform = pos;
            }, 0);
          })
          .addTo(controller);

          scenes.push(scenePara);

      });
    }

    return () => {
      scenes.forEach(scene => scene.destroy(true));
      controller.destroy(true);
      window.removeEventListener('scroll', handleScroll)
    };
  }, [])

  const rootClasses = classNames("immersive-image", getOverallSettingsClasses(fields));
  const rootId = componentId(rendering);

  const firstSlideTitleClasses = classNames({
    [params?.pc_TextAlignment]: !isMobile,
    [params?.pc_TextPosition]: !isMobile,
  })
  const firstSlideTitleClasses_m = classNames({
    [params?.m_TextAlignment]: isMobile,
    [params?.m_TextPosition]: isMobile,
  })


  return (
    <div className={rootClasses} datasourceid={rendering.dataSource} ref={rootNode} id={rootId}>
      <div className="q-container">
        <div className="immersive-image__title">
          <div ref={titleEl} className={`immersive-image__title__main ${params?.UppercaseTitle ? 'text-uppercase' : ''}`}>
            <Text field={title}></Text>
          </div>
          <div ref={subTitleEl} className="immersive-image__title__sub">
            <RichText field={isMobile && m_text ? m_text : pc_text}></RichText>
          </div>
        </div>
      </div>


      <div className="immersive-image__image-content" ref={imageEl}>
        {!isMobile && <BackgroundImage {...pc_image} className="immersive-image__image-content__image" />}
        {isMobile && <BackgroundImage {...m_image} className="immersive-image__image-content__image" />}
      
        <div className={`immersive-image__text-content ${firstSlideTitleClasses} ${firstSlideTitleClasses_m}`} ref={textEl}>
          <div>
            {title2 && <Text tag="div" className={`immersive-image__text-content__title ${params?.UppercaseTitle ? 'text-uppercase' : ''}`} field={title2} />}
            <RichText tag="div" className="immersive-image__text-content__text" field={isMobile && m_text2 ? m_text2 : pc_text2} />
          </div>
        </div>
      </div>

      {/* Follow up images */}
      <div className="follow-up-images" ref={followUpEl}>
        {
          !!list?.length && list.map((item, index) => {
            const {pc_image, m_image, pc_text, m_text, title} = item.fields;
            const bgImage = isMobile ? m_image : pc_image;
            const slideTitleClasses = classNames(
              {
                [item?.fields?.pc_TextAlignment?.value??'']: !isMobile,
                [item?.fields?.pc_TextPosition?.value??'']: !isMobile,
              }
            );
            const slideTitleClasses_m = classNames(
              {
                [item?.fields?.m_TextAlignment?.value??'']: isMobile,
                [item?.fields?.m_TextPosition?.value??'']: isMobile,
              }
            );
            
            return <div className={`immersive-image__image-content ${index === list.length - 1 ? 'last-slide' : ''}`} key={index}>
              <BackgroundImage ref={bgImgEl} {...bgImage} className="immersive-image__image-content__image" />
            
              <div className={`immersive-image__text-content ${slideTitleClasses} ${slideTitleClasses_m}`}>
                <div>
                  {title && <Text tag="div" className={`immersive-image__text-content__title ${params?.UppercaseTitle ? 'text-uppercase' : ''}`} field={title} />}
                  <RichText tag="div" className={`immersive-image__text-content__text`} field={isMobile && m_text ? m_text : pc_text} />
                </div>
              </div>
            </div>
          })
        }

        <div className="empty" ref={emptyEl}></div>
      </div>
    </div>
  );
};

export default 	ImmersiveImage;